import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '../../_services/auth.service';
import { CalcConfigService } from '_services/calc-config.service';
import { QuestionsService } from '_services/questions.service';
import { Question } from '../../_models/question';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { User } from '../../_models/user';
import { QuestionComponent } from '../question/question.component';
import { MatDialog } from '@angular/material/dialog';
import { HelpComponent } from '../../_shared/help/help.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';
import { MatCard, MatCardContent, MatCardTitle, MatCardSubtitle } from '@angular/material/card';
import { MatFormField, MatLabel } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { UsageComponent } from '../usage/usage.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { FilterPipe } from '../../_pipes/filter.pipe';
import { firstValueFrom } from 'rxjs';
import { applyMinimumMethodologyQuestions } from '../../_services/minimum-questions';
import { MinimumQuestionsDialogComponent } from './minimum-questions-dialog.component';

interface QuestionTab {
  label: string;
  code: string;
  adminOnly?: boolean;
}

@Component({
    selector: 'app-questions',
    templateUrl: './questions.component.html',
    styleUrls: ['./questions.component.css'],
    imports: [HelpComponent, MatButton, MatIcon, AidYearComponent, MatTabGroup, MatTab, MatCard, MatCardContent, NgClass, MatFormField, MatLabel, MatSelect, MatOption, UsageComponent, MatCardTitle, MatCardSubtitle, MatCheckbox, FormsModule, AsyncPipe, FilterPipe]
})
export class QuestionsComponent implements OnInit {
  user: Promise<User | null> = Promise.resolve(null);
  private isAdmin = false;

  filterFunction: ((item: Question) => boolean) | null = null;
  unusedFilterFunction: ((item: Question) => boolean) | null = null;

  schoolId = '';
  showHelp = false;
  questionsCopy: Question[] = [];
  editMode = false;

  /** Starting (ST) removed — remapped on load. Dependency (DS) is super-admin only. */
  private readonly allTabs: QuestionTab[] = [
    { label: 'School Specific', code: 'SI' },
    { label: 'Dependency', code: 'DS', adminOnly: true },
    { label: 'Parent Household', code: 'HP' },
    { label: 'Parent Income', code: 'PI' },
    { label: 'Parent Assets', code: 'PA' },
    { label: 'Student Finances', code: 'SF' },
    { label: 'All', code: 'ALL' },
  ];

  tabs: QuestionTab[] = this.allTabs.filter(tab => !tab.adminOnly);
  tabCode = this.tabs[0].code;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    public qs: QuestionsService,
    private cc: CalcConfigService,
    private auth: AuthService,
    public dialog: MatDialog
  ) {
    this.titleService.setTitle('Questions' + ' | NPC Admin');
  }

  ngOnInit() {
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.user = this.auth.currentUser();
    void this.user.then(user => {
      this.isAdmin = !!user?.admin;
      this.tabs = this.allTabs.filter(tab => !tab.adminOnly || this.isAdmin);
      if (!this.tabs.some(tab => tab.code === this.tabCode)) {
        this.tabCode = this.tabs[0].code;
      }
      this.setFilter(this.tabCode);
    });
    this.setFilter(this.tabCode);
  }

  /** School users never see Starting or Dependency questions (still kept in draft data). */
  private isVisibleToCurrentUser(item: Question): boolean {
    return this.isAdmin || (item.section_id !== 'DS' && item.section_id !== 'ST');
  }

  private setFilter(code: string): void {
    this.filterFunction =
      code === 'ALL'
        ? (item => this.isVisibleToCurrentUser(item))
        : (item => item.section_id === code && this.isVisibleToCurrentUser(item));

    this.unusedFilterFunction = this.filterFunction;
    if (code === 'SI') {
      this.unusedFilterFunction = q =>
        q.section_id === 'SI' &&
        (q.availableCustom === true || q.schoolCode === this.schoolId) &&
        this.isVisibleToCurrentUser(q);
    }
  }

  onTabChanged($event: { tab: { textLabel: string } }) {
    const match = this.tabs.find(tab => tab.label === $event.tab.textLabel);
    this.tabCode = match?.code ?? this.tabs[0].code;
    this.setFilter(this.tabCode);
  }

  addQuestion(q: Question) {

    const triggeredQuestions = this.qs.allQuestions.value.filter(tq => tq.triggerQuestionId === q.id);

    // Set Method Required Status
    const im = this.cc.draftRules.value?.schoolProperties?.institutionalMethod;
    q.methodRequired = im ? q.imRequired || false : q.fmRequired || false;


    const questions = this.qs.draftQuestions.value;
    // add question
    questions.push(q);
    // add triggered questions
    triggeredQuestions.forEach(tq => {
      questions.push(tq);
    });

    // Sort questions after adding a new question according to their sort order
    questions.sort((a, b) => a.ordinal - b.ordinal);
    this.qs.draftQuestions.next(questions);
    this.qs.populateUnusedQuestions();
    this.setFilter(this.tabCode);
  }

  removeQuestion(q: Question) {
    // Make sure there are no triggered questions that are being used prior to removal
    const triggeredQuestions = this.qs.draftQuestions.value.filter(tq => tq.triggerQuestionId === q.id);
    let usedBy = false;
    triggeredQuestions.forEach(tq => {
      const index = this.cc.usedBy.value.findIndex(ub => ub.entityId === tq.id);
      usedBy = usedBy || index !== -1;
      if (index !== -1) {
        alert('The question: ' + tq.label + ' - is triggered by the question you are trying to remove and being used in your calculations.  Please clear any uses before removing.');
      }
    });

    if (!usedBy) {
      const questionInUse = this.cc.usedBy.value.some(
        ub => ub.entityId === q.id && ub.entityType === 'Question'
      );
      if (!questionInUse) {
        const questions = this.qs.draftQuestions.value;

        // Remove question
        const index = questions.findIndex(question => question.id === q.id);
        questions.splice(index, 1);

        // Remove any triggered questions
        triggeredQuestions.forEach(tq => {
          const tqIndex = questions.findIndex(question => question.id === tq.id);
          questions.splice(tqIndex, 1);
        });

        this.qs.draftQuestions.next(questions);
        this.qs.populateUnusedQuestions();
        this.setFilter(this.tabCode);
      } else {
        alert('This question is being used. Please clear any uses before removing the question');
      }
    }
  }

  async clickedRow(question?: Question) {
    if (question && !this.editMode) {

      const user = await this.auth.currentUser();

      if (user?.admin) {
        const dialogRef = this.dialog.open(QuestionComponent, {
          width: '1050px',
          data: { question: question }
        });

        dialogRef.afterClosed().subscribe(saveQuestion => {
          if (saveQuestion?.id) {
            const questions = this.qs.draftQuestions.value;
            const index = questions.findIndex(item => item.id === saveQuestion.id);
            if (index === -1) {
              return;
            }
            questions[index] = saveQuestion;
            this.saveDraftQuestions(questions);
            this.setFilter(this.tabCode);
          }
        });

      }
    }
  }

  setMode(mode: 'edit' | 'save' | 'cancel') {
    switch (mode) {
      case 'edit':
        this.questionsCopy = JSON.parse(JSON.stringify(this.qs.draftQuestions.value));
        this.editMode = true;
        break;
      case 'cancel':
        this.qs.draftQuestions.next(this.questionsCopy);
        this.editMode = false;
        break;
      case 'save':
        if (JSON.stringify(this.questionsCopy) !== JSON.stringify(this.qs.draftQuestions.value)) {
          this.saveDraftQuestions(this.qs.draftQuestions.value);
        }
        this.editMode = false;
        break;
    }
  }

  async applyMinimumQuestions(): Promise<void> {
    const user = await this.user;
    if (!user?.admin) {
      return;
    }

    const aidYear = this.qs.loadedAidYear;
    if (aidYear == null) {
      alert('This school\'s questions are still loading.');
      return;
    }
    if (this.qs.allQuestionsLoadedAidYear !== aidYear || this.qs.allQuestions.value.length === 0) {
      alert('The master list of questions for this aid year is not loaded yet.');
      return;
    }

    const institutionalMethod = this.cc.draftRules.value?.schoolProperties?.institutionalMethod ?? false;
    const methodologyName = institutionalMethod ? 'Institutional Methodology' : 'Federal Methodology';
    const usedQuestionIds = new Set(
      this.cc.usedBy.value
        .filter(item => item.entityType === 'Question')
        .map(item => item.entityId),
    );
    const result = applyMinimumMethodologyQuestions(
      this.qs.draftQuestions.value,
      this.qs.allQuestions.value,
      institutionalMethod,
      usedQuestionIds,
    );

    const signature = (questions: Question[]) =>
      [...questions]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map(question => `${question.id}:${question.methodRequired}`)
        .join('|');
    if (signature(this.qs.draftQuestions.value) === signature(result.questions)) {
      alert(`Questions already match the minimum for ${methodologyName}.`);
      return;
    }

    const dialogRef = this.dialog.open(MinimumQuestionsDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: {
        methodologyName,
        added: result.added,
        removed: result.removed,
      },
    });
    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    try {
      await this.saveDraftQuestions(result.questions);
    } catch (err) {
      console.error('Failed to apply minimum questions', err);
      alert('Unable to save questions.');
    }
  }

  private saveDraftQuestions(questions: Question[]): Promise<void> {
    const aidYear = this.qs.loadedAidYear;
    if (aidYear == null) {
      return Promise.resolve();
    }
    return this.qs.saveQuestions(this.schoolId, questions, aidYear);
  }
}
